"use client"

import React from 'react'
import { Control, FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form'
import SelectInput, { SelectInputProps } from '../inputs/select-input'

/** Map Radix string values back to RHF: plain integer strings → number (e.g. region id); else unchanged. */
function valueFromSelectString(v: string): string | number | undefined {
    if (v === '') return undefined
    if (/^-?\d+$/.test(v)) {
        const n = Number(v)
        if (Number.isSafeInteger(n)) return n
    }
    return v
}
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormSelectInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormSelectInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = SelectInputProps & {
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>,
    /**
     * Runs only when the user changes the dropdown.
     * Return false to keep the current value.
     */
    onUserChange?: (nextValue: string) => boolean,
}

/**
 * A form select input component that integrates with React Hook Form
 * 
 * This component renders a dropdown select input with form integration.
 * It wraps the SelectInput component with React Hook Form functionality.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.selectProps - Additional select input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormSelectInput
 *   name="country"
 *   control={form.control}
 *   options={[
 *     { label: "United States", value: "us" },
 *     { label: "Canada", value: "ca" },
 *     { label: "United Kingdom", value: "uk" }
 *   ]}
 *   inputGroupProps={{
 *     label: "Country",
 *   }}
 * />
 * ```
 * 
 * @returns JSX.Element - The rendered select input component
 */
function FormSelectInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormSelectInputProps<TFieldValues, TName>) {
    const { inputGroupProps, name, control, loading, onUserChange, ...inputProps } = props

    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    // Controller for form field integration
    const { field, fieldState } = useController({
        name,
        control,
    })
    
    const { error } = fieldState

    return (
        <SelectInput
            {...inputProps}
            name={field.name}
            onChange={(v) => {
                if (onUserChange && onUserChange(v) === false) {
                    return
                }
                field.onChange(valueFromSelectString(v) as Parameters<typeof field.onChange>[0])
            }}
            onBlur={field.onBlur}
            value={field.value ?? undefined}
            ref={field.ref}
            disabled={isReadOnly || inputProps.disabled}
            loading={loading}

            inputGroupProps={{
                ...props.inputGroupProps,
                error: error?.message,
                isDirty: isDirty,
            }}
        />
    )
}

export default FormSelectInput